import { useEffect, useState, type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router'
import { CheckCircle2Icon, CircleDotIcon, CircleIcon, PartyPopperIcon, XIcon } from 'lucide-react'
import { z } from 'zod'
import { FINAL_STATES, type OrderState, type OrderView } from '@hedgehog/shared'
import { formatDate, formatDateTime } from '@/components/app/format'
import { CUSTOMER_STEP_LABELS, HAPPY_PATH, useOrder } from '@/components/app/order'
import { Button } from '@/components/ui/button'
import { useInterval } from '@/hooks/use-interval'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/o/$orderId/')({
  validateSearch: z.object({ paid: z.coerce.number().optional().catch(undefined) }),
  component: StatusPage,
})

const PRE_PAYMENT = new Set<OrderState>(['audited', 'preview_requested', 'preview_ready'])

function StatusPage() {
  const order = useOrder()
  const { paid } = Route.useSearch()
  const navigate = useNavigate()
  const router = useRouter()

  // Back from checkout, before the payment provider has told us: the banner promises the page updates
  // by itself. It stops after 10 minutes, and the banner then says so.
  const [waited, setWaited] = useState(false)
  useEffect(() => {
    if (paid !== 1) return
    const timer = setTimeout(() => setWaited(true), 10 * 60_000)
    return () => clearTimeout(timer)
  }, [paid])
  useInterval(
    () => void router.invalidate({ filter: (m) => m.routeId === '/o/$orderId' }),
    paid === 1 && !waited && PRE_PAYMENT.has(order.state) ? 5000 : null,
  )

  return (
    <div className="flex flex-col gap-8">
      {paid === 1 && (
        <div role="status" className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
          <PartyPopperIcon className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">Thank you for your payment.</p>
            <p className="text-sm text-muted-foreground">
              {PRE_PAYMENT.has(order.state)
                ? waited
                  ? 'This is taking longer than usual. We email you a receipt when it is confirmed. Refresh this page to see the latest state.'
                  : 'We are confirming it with our payment provider. This page updates by itself, usually within a minute, and we email you a receipt.'
                : 'We are rebuilding your full site now. We email you when it is ready for you to check, usually within 24 hours.'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Dismiss"
            onClick={() => void navigate({ to: '.', search: (prev) => ({ ...prev, paid: undefined }), replace: true })}
          >
            <XIcon />
          </Button>
        </div>
      )}

      <section aria-labelledby="progress" className="flex flex-col gap-4">
        <h2 id="progress" className="text-xl font-semibold">
          Progress
        </h2>
        <Timeline order={order} />
      </section>

      <Details order={order} />
    </div>
  )
}

interface Step {
  state: OrderState
  at: number | null
  status: 'done' | 'current' | 'next'
}

/** Past entries in the order they happened, then the remaining steps of the normal path. */
function buildSteps(order: OrderView): Step[] {
  const past: Step[] = order.timeline.map((e, i) => ({
    state: e.state,
    at: e.at,
    status: i === order.timeline.length - 1 && e.state === order.state ? 'current' : 'done',
  }))
  if (past.length === 0 || past[past.length - 1]?.state !== order.state) {
    past.push({ state: order.state, at: null, status: 'current' })
  }
  if (FINAL_STATES.has(order.state)) return past
  const reached = Math.max(-1, ...past.map((s) => HAPPY_PATH.indexOf(s.state)))
  const upcoming = HAPPY_PATH.slice(reached + 1).map<Step>((state) => ({ state, at: null, status: 'next' }))
  return [...past, ...upcoming]
}

function Timeline({ order }: { order: OrderView }) {
  const steps = buildSteps(order)
  return (
    <ol className="flex flex-col">
      {steps.map((s, i) => (
        <li key={`${s.state}-${i}`} className="relative flex gap-3 pb-5 last:pb-0" aria-current={s.status === 'current' ? 'step' : undefined}>
          {i < steps.length - 1 && <span aria-hidden className="absolute top-6 bottom-0 left-[9px] w-px bg-border" />}
          {s.status === 'done' ? (
            <CheckCircle2Icon className="relative size-5 shrink-0 text-success" aria-hidden />
          ) : s.status === 'current' ? (
            <CircleDotIcon className="relative size-5 shrink-0 text-foreground" aria-hidden />
          ) : (
            <CircleIcon className="relative size-5 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <div className="flex flex-col">
            <span className={cn(s.status === 'next' && 'text-muted-foreground', s.status === 'current' && 'font-medium')}>
              {CUSTOMER_STEP_LABELS[s.state]}
              <span className="sr-only">
                {s.status === 'done' ? ', done' : s.status === 'current' ? ', current step' : ', coming up'}
              </span>
            </span>
            {s.at !== null && <span className="text-sm text-muted-foreground">{formatDateTime(s.at)}</span>}
          </div>
        </li>
      ))}
    </ol>
  )
}

function Details({ order }: { order: OrderView }) {
  const rows: Array<[string, ReactNode]> = [['Current site', <a key="u" href={order.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">{order.domain}</a>]]
  if (order.email) rows.push(['Email', order.email])
  if (order.state === 'preview_ready' && order.previewToken) {
    rows.push([
      'Preview',
      <Link key="p" to="/preview/$token" params={{ token: order.previewToken }} className="underline underline-offset-4">
        See your homepage preview
        {order.previewExpiresAt ? ` (until ${formatDate(order.previewExpiresAt)})` : ''}
      </Link>,
    ])
  }
  if (order.approvedAt) rows.push(['Approved', formatDateTime(order.approvedAt)])
  if (order.contactEmail) rows.push(['Contact form sends to', order.contactEmail])
  if (order.site?.siteUrl)
    rows.push([
      'New site',
      <a key="s" href={order.site.siteUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
        {order.site.hostname ?? order.site.siteUrl}
      </a>,
    ])
  if (order.site?.liveAt) rows.push(['Live since', formatDateTime(order.site.liveAt)])

  return (
    <section aria-labelledby="details" className="flex flex-col gap-3">
      <h2 id="details" className="text-xl font-semibold">
        Details
      </h2>
      <dl className="divide-y divide-border rounded-xl border border-border">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
            <dt className="text-sm text-muted-foreground sm:w-48 sm:shrink-0">{k}</dt>
            <dd className="min-w-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
